import { InputText } from "primereact/inputtext";
import styles from "./Dashboard.module.css";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { ReportStatus } from "../../../api/types";
import { SpecialtyReport } from "../SpecialtyReport";
import { MultiSelect } from "primereact/multiselect";
import { Button } from "primereact/button";
import { DashboardLoadingRows } from "./DashboardRow";
import {
  appetiteScoreForReportInfo,
  borderColorForAttributes,
  readableStatus,
} from "./util";
import { useReports, useSafeReports, useUserById, useUsers } from "../data";
import * as client from "../../../client";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { ROUTES } from "../../../routes";
import { AppetiteModal } from "../appetite/AppetiteModal";
import { DashboardAnalytics } from "./Analytics";
import { PantheonAvatar } from "../../../components/avatar/Avatar";
import { displayNameForUser } from "../../../util";
import { SearchAddresses } from "./SearchAddresses";
import { Menu } from "primereact/menu";
import React from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { AppetiteIcon } from "../appetite/AppetiteIcon";
import { StatusIcon } from "./StatusIcon";
import { formatAbbreviatedMoney } from "../Money";
import { ThemeContext } from "../../../themes/Theme";
import { ReportActionMenu } from "./ReportActionMenu";
import { SortOrder } from "primereact/api";
import { SafeReportMetadataResponse } from "../Conversion";
import { useLogUIEventOnMount } from "../../../amplitude";
import {
  DEFAULT_FILTER,
  deserializeFilter,
  Filter,
  serializeFilter,
  SORT_FIELD_NAME,
} from "./filter";
import { MenuItem } from "primereact/menuitem";
import { isEqual } from "lodash";
import { AddFilterModal, ManageFilterModal } from "./FilterModal";
import { useDefaultFilter, useFilters } from "./filter-hooks";
import {
  fakeCompanyNameFromCompanyName,
  usePrivacyMode,
} from "../../../context-providers/Privacy";
import { MapModalForSearch } from "../map/MapModal";
import { useMapModal } from "../map/MapModalContext";

type StatusOption = {
  name: string;
  code: ReportStatus;
};

type UserOption = { name: string; id: string };
const UNASSIGNED_USER: UserOption = { name: "Unassigned", id: "Unassigned" };

export const Dashboard = ({ clientId }: { clientId: string }) => {
  return (
    <Routes>
      <Route
        path="report/:reportId/:tabPath?/:tabInfo?"
        element={<SpecialtyReport clientId={clientId} />}
      />
      <Route path="analytics" element={<DashboardAnalytics />} />
      <Route path="search-addresses" element={<SearchAddresses />} />
      <Route path="reports/casualty" element={<Reports isCasualty={true} />} />
      <Route path="reports/property" element={<Reports isCasualty={false} />} />
      <Route path="*" element={<Navigate to={ROUTES.DASHBOARD_PROPERTY} />} />
    </Routes>
  );
};

type ReportsProps = {
  isCasualty: boolean;
};

const COLUMN_NAME_MAPPING: [SORT_FIELD_NAME, string][] = [
  ["company_name", "report_metadata.company_info.company_name"],
  ["broker_name", "report_metadata.company_info.broker_name"],
  ["eff_date", "report_metadata.company_info.effective_date"],
  ["status", "status"],
  ["tiv", "report_metadata.sov_metadata.total_tiv"],
  ["creation_date", "created_at_ts"],
];

const Reports: React.FC<ReportsProps> = ({ isCasualty }) => {
  const apiReports = useReports();
  const specialtyReports = useSafeReports().data ?? [];
  const {
    visible: isMapModalVisible,
    showMapModal,
    hideMapModal,
  } = useMapModal();
  useLogUIEventOnMount("acropolis_show_dashboard");

  const location = useLocation();
  const navigate = useNavigate();

  const [filter, setFilterRaw] = useState<Filter>(() =>
    deserializeFilter(location.search)
  );
  const defaultFilter = useDefaultFilter();
  useEffect(() => {
    if (defaultFilter && location.search === "") {
      setFilterRaw(defaultFilter);
    }
  }, [defaultFilter]);

  const setFilter = <K extends keyof Filter>(
    field: K,
    value: Filter[K],
    setField = true
  ) => {
    setFilterRaw((f) => {
      return {
        ...f,
        [field]: value,
        setFields: setField
          ? Array.from(new Set(f.setFields.concat(field)))
          : f.setFields.filter((f) => f !== field),
      };
    });
  };

  useEffect(() => {
    navigate(ROUTES.DASHBOARD_PROPERTY + "?" + serializeFilter(filter));
  }, [filter]);

  const [searchText, setSearchText] = useState("");
  const users = useUsers();
  const selectedAssigneeUsers = filter.selectedAssigneeUserIds
    .map((id) => {
      if (id === "Unassigned") {
        return UNASSIGNED_USER;
      } else {
        const u = users?.data?.all_company_users.find((u) => u.id === id);
        return u ? { name: displayNameForUser(u), id: u.id } : undefined;
      }
    })
    .filter((u) => !!u);
  const selectedHandlerUsers = filter.selectedHandlerUserIds
    .map((id) => {
      if (id === "Unassigned") {
        return UNASSIGNED_USER;
      } else {
        const u = users?.data?.all_company_users.find((u) => u.id === id);
        return u ? { name: displayNameForUser(u), id: u.id } : undefined;
      }
    })
    .filter((u) => !!u);

  const statuses = Object.values(ReportStatus).map((status) => {
    return {
      name: readableStatus(status),
      code: status,
    };
  });
  const selectedStatuses = filter.selectedStatuses
    .map((status) => statuses.find((s) => s.code === status))
    .filter((s) => !!s);

  const initialSortField = filter.initialSortField;
  const initialSortOrder =
    filter.initialSortOrder === "asc" ? SortOrder.ASC : SortOrder.DESC;

  const [
    selectedReportIdForBusinessClassification,
    setSelectedReportIdForBusinessClassification,
  ] = useState<string | undefined>(undefined);
  const selectedReportForBusinessClassification = specialtyReports.find(
    (report) => report.report_id === selectedReportIdForBusinessClassification
  );
  const [manageFilterModalOpen, setManageFilterModalOpen] = useState(false);
  const [addFilterModalState, setAddFilterModalState] = useState<Filter | null>(
    null
  );

  const { theme } = useContext(ThemeContext);
  const isLightMode = theme === "light";

  const filteredAndSortedReports = useMemo(() => {
    const filteredReports = specialtyReports.filter((report) => {
      if (selectedStatuses.length > 0) {
        if (!selectedStatuses.some((status) => status.code === report.status)) {
          return false;
        }
      }

      if (selectedAssigneeUsers.length > 0) {
        if (
          !selectedAssigneeUsers.some(
            (user) =>
              (user.id === "Unassigned" && !report.assignee) ||
              user.id === report.assignee
          )
        ) {
          return false;
        }
      }

      if (selectedHandlerUsers.length > 0) {
        const handler = report.additional_assignees?.assignees["handler"];
        if (
          !selectedHandlerUsers.some(
            (user) =>
              (user.id === "Unassigned" && !handler) || user.id === handler
          )
        ) {
          return false;
        }
      }

      if (searchText !== "") {
        const searchTextLower = searchText.toLowerCase();
        if (
          // TODO: what else should we search on?
          !report.report_metadata.company_info.company_name
            .toLowerCase()
            .includes(searchTextLower) &&
          !report.report_metadata.company_info.broker_name
            .toLowerCase()
            .includes(searchTextLower)
        ) {
          return false;
        }
      }

      return true;
    });

    return filteredReports;
  }, [
    selectedStatuses,
    specialtyReports,
    searchText,
    selectedAssigneeUsers,
    isCasualty,
  ]);

  const menuRef = useRef<Menu>(null);
  const columnPtOptions = { bodyCell: { style: { padding: "6px 8px" } } };

  const savedFilters = useFilters();
  const isUsingSavedQuery = savedFilters.find(({ filter: saved }) =>
    isEqual(filter, saved)
  );

  const model: MenuItem[] = (
    [
      {
        label: "Filters",
        items: [
          {
            label: isUsingSavedQuery
              ? `Currently using "${isUsingSavedQuery.name}"`
              : "Not using a filter",
            disabled: true,
          },
          { label: "Manage", command: () => setManageFilterModalOpen(true) },
          ...(isUsingSavedQuery
            ? [
                {
                  label: "Clear current filter",
                  command: () => setFilterRaw(DEFAULT_FILTER),
                },
              ]
            : [
                {
                  label: "Save current view as filter",
                  command: () => setAddFilterModalState(filter),
                },
              ]),
        ],
      },
      { separator: true },
    ] as MenuItem[]
  ).concat(
    savedFilters.map(
      ({ name, filter, isDefault }): MenuItem => ({
        label: name + (isDefault ? " (default)" : ""),
        command: () => setFilterRaw(filter),
      })
    )
  );

  const filterMenuRef = useRef<Menu | null>(null);
  const { privacyModeEnabled } = usePrivacyMode();

  return (
    <>
      <div className={styles.container}>
        <div className={styles.searchContainer}>
          <InputText
            className={styles.searchInput}
            placeholder="Search"
            value={searchText}
            onChange={(e) => setSearchText(e.currentTarget.value)}
          />
          <div className={styles.filterButton}>
            <Button
              style={{
                padding: "4px 12px",
                minWidth: "32px",
                height: "32px",
                borderRadius: "16px",
              }}
              icon={isUsingSavedQuery ? "pi pi-filter-fill" : "pi pi-filter"}
              tooltip="Saved filters"
              tooltipOptions={{ showDelay: 500 }}
              severity="secondary"
              outlined
              onClick={(e) => filterMenuRef.current?.toggle(e)}
              iconPos="right"
              label={isUsingSavedQuery?.name}
            />
            <Menu
              model={model}
              ref={filterMenuRef}
              popup
              popupAlignment="left"
              style={{ minWidth: "300px" }}
            />
          </div>
        </div>
        <div className={styles.rowsContainer}>
          <DataTable
            scrollable
            value={filteredAndSortedReports}
            scrollHeight="calc(100vh - 234px)"
            rowClassName={() => styles.row}
            paginator
            rows={30}
            sortField={
              COLUMN_NAME_MAPPING.find(
                ([field]) => field === initialSortField
              )?.[1]
            }
            sortOrder={initialSortOrder}
            onSort={(properties) => {
              const order = properties["sortOrder"];
              const field = properties["sortField"];
              if (order) {
                setFilter("initialSortOrder", order === 1 ? "asc" : "desc");
              }
              if (field) {
                const sort = COLUMN_NAME_MAPPING.find(
                  ([, path]) => path === field
                )?.[0];
                if (sort) {
                  setFilter("initialSortField", sort);
                } else {
                  throw new Error(`Unrecognized sort field ${field}`);
                }
              }
            }}
            pt={{
              headerRow: {
                style: { boxShadow: "0px 0px 8px rgba(0, 0, 0, 0.15)" },
              },
            }}
            emptyMessage={
              apiReports.isPending ? (
                <DashboardLoadingRows />
              ) : (
                "No results found"
              )
            }
            onRowClick={(event) => {
              const report = event.data as client.ReportMetadataResponse;
              navigate(ROUTES.DASHBOARD_REPORT(report.report_id, "review"));
            }}
          >
            <Column
              style={{ width: "32px", padding: "16px 8px" }}
              header=""
              pt={columnPtOptions}
            />
            <Column
              style={{ width: "160px", padding: "16px 8px" }}
              header={() => (
                <div className={styles.horizontalContainer}>
                  <AssigneeFilterDropdown
                    icon={"pi pi-user-edit"}
                    selectedUsers={selectedAssigneeUsers}
                    setSelectedUsers={(users) =>
                      setFilter(
                        "selectedAssigneeUserIds",
                        users.map((u) => u.id)
                      )
                    }
                  />
                  <span>Underwriter</span>
                </div>
              )}
              body={(report: SafeReportMetadataResponse) => {
                const assignee = useUserById(report.assignee);
                return (
                  <PantheonAvatar
                    label={displayNameForUser(assignee)}
                    showName={true}
                  />
                );
              }}
              pt={columnPtOptions}
            />
            <Column
              style={{ width: "160px", padding: "16px 8px" }}
              header={() => (
                <div className={styles.horizontalContainer}>
                  <AssigneeFilterDropdown
                    icon={"pi pi-users"}
                    selectedUsers={selectedHandlerUsers}
                    setSelectedUsers={(users) =>
                      setFilter(
                        "selectedHandlerUserIds",
                        users.map((u) => u.id)
                      )
                    }
                  />
                  <span>Handler</span>
                </div>
              )}
              body={(report: SafeReportMetadataResponse) => {
                const handler = useUserById(
                  report.additional_assignees?.assignees["handler"]
                );
                return (
                  <PantheonAvatar
                    label={displayNameForUser(handler)}
                    showName={true}
                  />
                );
              }}
              pt={columnPtOptions}
            />
            <Column
              header="Company Name"
              style={{ width: "250px", padding: "16px 8px" }}
              field="report_metadata.company_info.company_name"
              sortable
              body={(report: SafeReportMetadataResponse) => {
                return (
                  <span className={styles.twoLineText}>
                    {privacyModeEnabled
                      ? fakeCompanyNameFromCompanyName(
                          report.report_metadata.company_info.company_name
                        )
                      : report.report_metadata.company_info.company_name}
                  </span>
                );
              }}
              pt={columnPtOptions}
            />
            <Column
              header="Appetite"
              style={{ width: "180px", padding: "16px 8px" }}
              body={(report: SafeReportMetadataResponse) => {
                return (
                  <AppetiteIcon
                    naicsInfo={report.report_metadata.naics_info}
                    justifyContent="flex-start"
                    fontSize="10px"
                    appetite={appetiteScoreForReportInfo(
                      report.report_metadata.business_classification,
                      report.report_metadata.appetite_score
                    )}
                    onClick={() =>
                      setSelectedReportIdForBusinessClassification(
                        report.report_id
                      )
                    }
                  />
                );
              }}
              pt={columnPtOptions}
            />
            <Column
              header="Brokerage"
              style={{ width: "186px", padding: "16px 8px" }}
              field="report_metadata.company_info.broker_name"
              sortable
              body={(report: SafeReportMetadataResponse) => {
                return (
                  <span className={styles.twoLineText}>
                    {report.report_metadata.company_info.broker_name}
                  </span>
                );
              }}
              pt={columnPtOptions}
            />
            <Column
              header="Effective Date"
              style={{ width: "160px", padding: "16px 8px" }}
              field="report_metadata.company_info.effective_date"
              sortable
              sortFunction={(event) => {
                const { data, order } = event;

                return [...data].sort((a, b) => {
                  const effectiveDateA =
                    a.report_metadata.company_info.effective_date;
                  const effectiveDateB =
                    b.report_metadata.company_info.effective_date;

                  // Check if dates are valid
                  const isValidA =
                    effectiveDateA &&
                    !isNaN(new Date(effectiveDateA).getTime());
                  const isValidB =
                    effectiveDateB &&
                    !isNaN(new Date(effectiveDateB).getTime());

                  // Handle cases where one or both dates are invalid
                  if (!isValidA && !isValidB) return 0;
                  if (!isValidA) return order === 1 ? -1 : 1;
                  if (!isValidB) return order === 1 ? 1 : -1;

                  // Both dates are valid, compare them normally
                  const dateA = new Date(effectiveDateA);
                  const dateB = new Date(effectiveDateB);
                  const comparison = dateA.getTime() - dateB.getTime();

                  return (order ?? 0) * comparison;
                });
              }}
              body={(report: SafeReportMetadataResponse) => {
                return (
                  <span>
                    {report.report_metadata.company_info.effective_date}
                  </span>
                );
              }}
              pt={columnPtOptions}
            />
            <Column
              style={{ width: "160px", padding: "16px 8px" }}
              field="status"
              sortable
              body={(report: SafeReportMetadataResponse) => {
                return <StatusIcon status={report.status} />;
              }}
              pt={columnPtOptions}
              header={() => (
                <div className={styles.horizontalContainer}>
                  <span>Status</span>
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    <StatusFilterDropdown
                      statuses={statuses}
                      selectedStatuses={selectedStatuses}
                      setSelectedStatuses={(statuses) =>
                        setFilter(
                          "selectedStatuses",
                          statuses.map((s) => s.code)
                        )
                      }
                    />
                  </div>
                </div>
              )}
            />
            <Column
              header="TIV"
              style={{ width: "112px", padding: "16px 8px" }}
              field="report_metadata.sov_metadata.total_tiv"
              sortable
              body={(report: SafeReportMetadataResponse) => {
                const totalTiv = report.report_metadata.sov_metadata.total_tiv;
                const difficulty =
                  totalTiv < 10_000_000
                    ? "good"
                    : totalTiv < 25_000_000
                    ? "medium"
                    : "bad";
                return (
                  <div style={{ display: "flex" }}>
                    <div className={styles.verticalContainer}>
                      <span>{formatAbbreviatedMoney(totalTiv)}</span>

                      <div
                        style={{
                          marginLeft: "-2px",
                          marginRight: "-2px",
                          height: "2px",
                          backgroundColor: borderColorForAttributes(
                            difficulty,
                            isLightMode
                          ),
                        }}
                      />
                    </div>
                  </div>
                );
              }}
              pt={columnPtOptions}
            />
            <Column
              style={{ width: "80px", padding: "16px 8px" }}
              header={<i className="pi pi-calendar" />}
              field="created_at_ts"
              body={(report: SafeReportMetadataResponse) => {
                const formatted = new Intl.DateTimeFormat("en-US", {
                  month: "short",
                  day: "numeric",
                }).format(new Date(report.created_at_ts + "Z"));
                return <span>{formatted}</span>;
              }}
              pt={columnPtOptions}
              sortable
            />
            <Column
              header={
                <>
                  <Menu
                    model={[
                      {
                        label: "Search bound policies",
                        icon: "pi pi-map",
                        command: showMapModal,
                      },
                    ]}
                    popup
                    ref={menuRef}
                    id="popup_menu"
                    popupAlignment="left"
                  />
                  <Button
                    icon="pi pi-ellipsis-v"
                    onClick={(event) => menuRef.current?.toggle(event)}
                    aria-controls="popup_menu"
                    aria-haspopup
                    text
                    severity="secondary"
                    style={{ color: "var(--text-color)" }}
                  />
                </>
              }
              style={{ width: "60px", padding: "16px 8px" }}
              body={(report: SafeReportMetadataResponse) => {
                return <ActionMenu report={report} />;
              }}
              pt={columnPtOptions}
            />
            <Column
              style={{ width: "32px", padding: "16px 8px" }}
              header=""
              pt={columnPtOptions}
            />
          </DataTable>
        </div>
      </div>
      {selectedReportForBusinessClassification && (
        <AppetiteModal
          report={selectedReportForBusinessClassification.report_metadata}
          visible={selectedReportForBusinessClassification !== undefined}
          onHide={() => setSelectedReportIdForBusinessClassification(undefined)}
        />
      )}
      <ManageFilterModal
        visible={manageFilterModalOpen}
        onHide={() => setManageFilterModalOpen(false)}
      />
      <AddFilterModal
        filter={addFilterModalState}
        onHide={() => setAddFilterModalState(null)}
      />
      {isMapModalVisible && (
        <MapModalForSearch visible={isMapModalVisible} onHide={hideMapModal} />
      )}
    </>
  );
};

const ActionMenu = ({ report }: { report: SafeReportMetadataResponse }) => {
  return <ReportActionMenu report={report} isUpcomingRenewal={false} />;
};

type StatusFilterDropdownProps = {
  statuses: StatusOption[];
  selectedStatuses: StatusOption[];
  setSelectedStatuses: (statuses: StatusOption[]) => void;
};

const StatusFilterDropdown: React.FC<StatusFilterDropdownProps> = ({
  statuses,
  selectedStatuses,
  setSelectedStatuses,
}) => {
  return (
    <MultiSelect
      value={selectedStatuses}
      onChange={(e) => setSelectedStatuses(e.value)}
      options={statuses}
      optionLabel="name"
      placeholder={""}
      maxSelectedLabels={3}
      className={styles.multiSelect}
      dropdownIcon={() => (
        <div className={styles.decorationWrapper}>
          <span className="pi pi-filter"></span>
          {selectedStatuses.length > 0 ? (
            <InUseDecoration count={selectedStatuses.length} />
          ) : null}
        </div>
      )}
    />
  );
};

type AssigneeFilterDropdownProps = {
  icon: string;
  selectedUsers: UserOption[];
  setSelectedUsers: (ids: UserOption[]) => void;
};

const avatarTemplate = ({ name }: { name: string }) => {
  return <PantheonAvatar label={name} showName={true} />;
};

const AssigneeFilterDropdown: React.FC<AssigneeFilterDropdownProps> = ({
  icon,
  selectedUsers,
  setSelectedUsers,
}) => {
  const users = useUsers();
  const options = [
    ...(users?.data?.all_company_users ?? []).map((u) => ({
      name: displayNameForUser(u),
      id: u.id,
    })),
    { name: "Unassigned", id: "Unassigned" },
  ];

  return (
    <MultiSelect
      value={selectedUsers}
      onChange={(e) => setSelectedUsers(e.value)}
      options={options}
      itemTemplate={avatarTemplate}
      optionLabel="name"
      placeholder={""}
      className={styles.multiSelect}
      dropdownIcon={() => (
        <div className={styles.decorationWrapper}>
          <span className={icon}></span>
          {selectedUsers.length > 0 ? (
            <InUseDecoration count={selectedUsers.length} />
          ) : null}
        </div>
      )}
    />
  );
};

const InUseDecoration = ({ count }: { count: number }) => (
  <span className={styles.decoration}>{count}</span>
);
